/* Kop van de projectpagina: nummer, stadium, naam, en de paar feiten die ertoe
 * doen. Alles is hier direct aan te passen (stadium, waarde, kans, sluitdatum);
 * er zit geen popup meer tussen de lijst en het project. */

import { useState, type ReactNode, type Ref } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Check, ExternalLink, MoreHorizontal, Pencil, Upload, Link2, ClipboardList, Archive } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { PROJECT_STAGES, getStageConfig } from '@/components/crm/project/projectUtils';
import { cn } from '@/lib/utils';
import type { PaginaProject } from '../data/project';
import type { Gekoppeld } from '../data/project';
import type { Monitor } from '../data/monitor';
import { useWijzigProject } from '../hooks';
import { datum, euro } from './opmaak';

const STADIUM_KLEUR: Record<string, string> = {
  verkennen: 'bg-blue-700 text-white',
  voorstel: 'bg-purple-700 text-white',
  gewonnen: 'bg-green-700 text-white',
  operatie: 'bg-emerald-800 text-white',
};

export function Feit({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex shrink-0 flex-col border-l pl-4 first:border-l-0 first:pl-0">
      <dt className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="m-0 text-[15px] font-semibold tabular-nums">{children}</dd>
    </div>
  );
}

/** Een waarde die je aanklikt om hem te wijzigen; Enter of wegklikken bewaart, Escape niet. */
export function Bewerkbaar({
  label, weergave, waarde, type, onBewaar, stap, invoerKlasse = 'h-7 w-36 text-sm',
}: {
  label: string;
  weergave: ReactNode;
  waarde: string;
  type: 'number' | 'date' | 'text';
  stap?: number;
  onBewaar: (v: string) => void;
  /** Klassen van het invoerveld tijdens het wijzigen (breedte, lettergrootte). */
  invoerKlasse?: string;
}) {
  const [bezig, setBezig] = useState(false);
  const [concept, setConcept] = useState(waarde);
  if (!bezig) {
    return (
      <button
        type="button"
        onClick={() => { setConcept(waarde); setBezig(true); }}
        className="group -mx-1 inline-flex max-w-full items-center gap-1.5 rounded px-1 text-left hover:bg-muted"
        aria-label={`${label} wijzigen`}
      >
        <span className="whitespace-nowrap">{weergave}</span>
        <Pencil className="h-3.5 w-3.5 shrink-0 text-muted-foreground opacity-0 group-hover:opacity-100" aria-hidden />
      </button>
    );
  }
  const klaar = (bewaar: boolean) => {
    setBezig(false);
    if (bewaar && concept !== waarde) onBewaar(concept);
  };
  return (
    <Input
      autoFocus
      aria-label={label}
      type={type}
      step={stap}
      value={concept}
      onChange={(e) => setConcept(e.target.value)}
      onBlur={() => klaar(true)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') klaar(true);
        if (e.key === 'Escape') klaar(false);
      }}
      className={invoerKlasse}
    />
  );
}

export function ProjectKop({
  project, monitor, gekoppeld, onBewerken, onBestandToevoegen, navigatie, kopRef,
}: {
  project: PaginaProject;
  monitor: Monitor | null | undefined;
  gekoppeld: Gekoppeld | undefined;
  onBewerken: () => void;
  onBestandToevoegen: () => void;
  /** Vorige/volgende en de projectwisselaar, links boven. */
  navigatie?: ReactNode;
  kopRef?: Ref<HTMLElement>;
}) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const wijzig = useWijzigProject(project.id);
  const [archiveren, setArchiveren] = useState(false);
  const stadium = project.stage ?? 'verkennen';
  const stadiumIndex = PROJECT_STAGES.indexOf(stadium as never);
  const inVerkoop = stadium === 'verkennen' || stadium === 'voorstel';
  const sharepoint = project.sharepoint_web_url ?? project.sharepoint_main_url;

  const bewaar = (w: Parameters<typeof wijzig.mutate>[0], wat: string) =>
    wijzig.mutate(w, {
      onError: (e) => toast({ title: `${wat} niet opgeslagen`, description: (e as Error).message, variant: 'destructive' }),
    });

  const kopieerLink = async () => {
    await navigator.clipboard.writeText(`${window.location.origin}/crm/projects/${project.id}`);
    toast({ title: 'Projectlink gekopieerd' });
  };

  const maakIntakeLink = async () => {
    const { data, error } = await supabase.functions.invoke('intake-invite', { body: { projectId: project.id, note: `Project: ${project.name}` } });
    if (error || !data?.intakeUrl) {
      toast({ title: 'Intake-link maken mislukt', description: error?.message ?? 'Geen link ontvangen.', variant: 'destructive' });
      return;
    }
    await navigator.clipboard.writeText(data.intakeUrl);
    toast({ title: 'Intake-link gekopieerd', description: 'De link staat op je klembord.' });
  };

  const archiveer = async () => {
    const { error } = await supabase.from('projects').update({ deleted_at: new Date().toISOString() }).eq('id', project.id);
    if (error) {
      toast({ title: 'Archiveren mislukt', description: error.message, variant: 'destructive' });
      return;
    }
    toast({ title: 'Project gearchiveerd', description: 'Je vindt het terug in het archief.' });
    navigate('/crm/projects');
  };

  /* De organisatie is een link naar haar pagina (ADR 0022). */
  const org = project.organization && project.organization.name !== project.name ? project.organization : null;
  const onderregel: ReactNode[] = [
    project.type,
    org ? <Link key="org" to={`/crm/organisaties/${org.id}`} data-org-link className="hover:text-foreground hover:underline">{org.name}</Link> : null,
    monitor ? [monitor.naam, monitor.plaats].filter(Boolean).join(', ') : project.city,
  ]
    .filter(Boolean)
    .flatMap((d, i) => (i ? [' · ', d] : [d]));

  const subsidie = gekoppeld?.subsidies[0];

  return (
    <section ref={kopRef} aria-label="Project" className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="-ml-2">{navigatie}</div>
        <div className="flex items-center gap-2">
          {sharepoint && (
            <Button variant="outline" size="sm" className="h-8" asChild>
              <a href={sharepoint} target="_blank" rel="noreferrer">
                <ExternalLink className="mr-1.5 h-4 w-4" />
                SharePoint
              </a>
            </Button>
          )}
          <Button size="sm" className="h-8" onClick={onBestandToevoegen}>
            <Upload className="mr-1.5 h-4 w-4" />
            Bestand toevoegen
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon" className="h-8 w-8" aria-label="Meer acties">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={onBewerken}><Pencil className="mr-2 h-4 w-4" />Bewerken</DropdownMenuItem>
              <DropdownMenuItem onSelect={kopieerLink}><Link2 className="mr-2 h-4 w-4" />Kopieer projectlink</DropdownMenuItem>
              <DropdownMenuItem onSelect={maakIntakeLink}><ClipboardList className="mr-2 h-4 w-4" />Maak intake-link</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => setArchiveren(true)}><Archive className="mr-2 h-4 w-4" />Archiveren</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="flex min-w-0 flex-col gap-0.5">
        <div className="flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-1">
          {project.project_number && <span className="font-mono text-sm text-muted-foreground">{project.project_number}</span>}
          <h1 className="min-w-0 text-2xl font-semibold leading-tight tracking-tight">{project.name}</h1>
          {project.status && project.status !== 'actief' && (
            <span className="rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium capitalize">{project.status}</span>
          )}
        </div>
        {onderregel.length > 0 && <p className="text-sm text-muted-foreground">{onderregel}</p>}
      </div>

      <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
        <div role="group" aria-label="Stadium" className="flex flex-wrap items-center gap-0.5 rounded-full border bg-background p-[3px] text-[13px]">
          {PROJECT_STAGES.map((s, i) => {
            const actief = s === stadium;
            const gedaan = stadiumIndex > -1 && i < stadiumIndex;
            return (
              <button
                key={s}
                type="button"
                aria-pressed={actief}
                disabled={wijzig.isPending}
                onClick={() => !actief && bewaar({ stage: s }, 'Stadium')}
                className={cn(
                  'inline-flex h-7 items-center gap-1.5 rounded-full px-3 transition-colors',
                  actief ? cn(STADIUM_KLEUR[s], 'font-semibold') : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                )}
              >
                {gedaan && <Check className="h-3 w-3" aria-hidden />}
                {getStageConfig(s).label}
              </button>
            );
          })}
        </div>
        <dl className="m-0 flex flex-wrap items-center gap-x-4 gap-y-2">
          <Feit label="Waarde">
            <Bewerkbaar
              label="Projectwaarde"
              type="number"
              stap={1000}
              waarde={project.amount === null ? '' : String(project.amount)}
              weergave={euro(project.amount)}
              onBewaar={(v) => bewaar({ amount: v === '' ? null : Number(v) }, 'Projectwaarde')}
            />
          </Feit>
          {inVerkoop && (
            <Feit label="Kans">
              <Bewerkbaar
                label="Kans"
                type="number"
                stap={5}
                waarde={project.probability === null ? '' : String(project.probability)}
                weergave={project.probability === null ? '—' : `${project.probability}%`}
                onBewaar={(v) => bewaar({ probability: v === '' ? null : Math.max(0, Math.min(100, Math.round(Number(v)))) }, 'Kans')}
              />
            </Feit>
          )}
          {inVerkoop && (
            <Feit label="Sluitdatum">
              <Bewerkbaar
                label="Sluitdatum"
                type="date"
                waarde={project.close_date ? project.close_date.slice(0, 10) : ''}
                weergave={project.close_date ? datum(project.close_date) : '—'}
                onBewaar={(v) => bewaar({ close_date: v || null }, 'Sluitdatum')}
              />
            </Feit>
          )}
          <Feit label="Eigenaar">{project.owner?.display_name ?? project.owner?.email ?? '—'}</Feit>
          {monitor?.netContractKw != null && (
            <Feit label="Net">
              {monitor.netContractKw} kW
              {monitor.sturingsdrempelKw != null && (
                <span className="text-xs font-normal text-muted-foreground"> · sturing {monitor.sturingsdrempelKw}</span>
              )}
            </Feit>
          )}
          {subsidie && (
            <Feit label="Subsidie">
              {subsidie.naam ?? 'Subsidie-intake'}
              {subsidie.volledigheid != null && <span className="text-xs font-normal text-muted-foreground"> · {subsidie.volledigheid}%</span>}
            </Feit>
          )}
        </dl>
      </div>

      <AlertDialog open={archiveren} onOpenChange={setArchiveren}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Project archiveren</AlertDialogTitle>
            <AlertDialogDescription>
              Het project verdwijnt uit de lijst maar blijft bewaard. Je kunt het later terughalen via het archief.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuleren</AlertDialogCancel>
            <AlertDialogAction onClick={archiveer}>Archiveren</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
